import type { components } from '@/generated/contracts/api-v1';
import { fieldMap, requireId, type FieldSpec, type LegacyRow } from './fieldMap';

// keyframes table; KeyframeDto.From in lureo-backend Data/Api/V1/SceneContent/KeyframeDtos.cs
export type KeyframeDto = components['schemas']['KeyframeDto'];
export type CreateKeyframeRequest = components['schemas']['CreateKeyframeRequest'];

const spec = {
  uniqueId: 'keyframe_id',
  assetInstanceUniqueId: 'asset_instance_id',
  // The asset instance's scene, which is what keyframes.scene_instance_id is written with
  sceneUniqueId: 'scene_instance_id',
  time: 'keyframe_time',
  position: 'keyframe_position',
  rotation: 'keyframe_rotation',
  scale: 'keyframe_scale',
  category: 'keyframe_category',
  createdAt: 'created_at',
} as const satisfies FieldSpec<KeyframeDto>;

const map = fieldMap<KeyframeDto, typeof spec>('Keyframe', spec);

export type LegacyKeyframe = LegacyRow<typeof spec, KeyframeDto>;

/** updateKeyframe / updateKeyframes accept these. */
export const KEYFRAME_PATCH_FIELDS = ['time', 'position', 'rotation', 'scale', 'category'] as const;
/** createKeyframe accepts these. */
export const KEYFRAME_CREATE_FIELDS = ['uniqueId', 'assetInstanceUniqueId', ...KEYFRAME_PATCH_FIELDS] as const;

export const keyframeToLegacy = (dto: KeyframeDto): LegacyKeyframe => map.toLegacy(dto);

/** The KeyframeDto for a legacy row read from v1, for messages that carry the v1 shape. */
export const keyframeFromLegacy = (row: LegacyKeyframe): KeyframeDto => map.toDto(row);

/** createKeyframe body from a legacy insert row; keyframe_id is required. */
export function keyframeCreateBody(row: Partial<LegacyKeyframe>): CreateKeyframeRequest {
  // The server writes the asset instance's scene and the creation time
  const body = map.toBody(row, KEYFRAME_CREATE_FIELDS, ['scene_instance_id', 'created_at']);
  return { ...body, uniqueId: requireId('Keyframe', 'keyframe_id', body.uniqueId) };
}

/** updateKeyframe body from a legacy partial update. */
export const keyframePatchBody = (changes: Partial<LegacyKeyframe>) => map.toBody(changes, KEYFRAME_PATCH_FIELDS);
